import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Form, Formik } from 'formik';
import { find, forEach, get, indexOf, keys, map, some, reduce, size } from 'lodash-es';

import GraduatedLoanBlock from '../GraduatedLoanBlock';
import Group from './Blocks/Group';
import Part from './Blocks/Part';
import Question from './Blocks/Question';
import HandleFields from './Fields';
import ErrorCustom from './Fields/ErrorCustom';

import useReducer from '../../Utils/useReducer';
import reducer from './QuestionnaireBlock.reducer';

import * as types from './QuestionnaireBlock.actionTypes';

import { getCodeFirstError } from '../../Utils/error';
import { getByField, getCorrectValue, getLabel } from '../../Utils/utils';
import { questionnaireShape } from './QuestionnaireBlock.shape';
import { initializeState } from './QuestionnaireBlock.transform';
import { initializeQuestionsFormik } from './QuestionnaireBlock.utils';
import { validate } from '../../Utils/validation';

import './QuestionnaireBlock.scss';

/**
 * questionnaire bloc custom
 * @returns {XML}
 * @constructor
 * @param initialState
 * @param id
 * @param graduatedLoans
 * @param values
 * @param errors
 * @param setFieldValue
 * @param isValid
 * @param resetForm
 * @param eventEmitter
 * @param willResetForm
 * @param onResetForm
 * @param className
 */
const QuestionnaireBlockCustom = ({
  initialState,
  id,
  graduatedLoans,
  formik: { values, errors, setFieldValue, isValid, resetForm },
  eventEmitter,
  willResetForm,
  onResetForm,
  className,
  validateFormGraduatedLoans,
  i18n,
  strings,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [willValidate, setWillValidate] = useState(false);

  /**
   * handle show group
   * @param idGroup
   * @param refQuestionsGroup
   * @param showGroup
   */
  const handleShowGroup = ({ questions, groups }) => {
    try {
      // get group item
      const getGroupItem = ({ id: groupId, refQuestions, show }) => {
        const reduceQuestionItem = (result, key) => {
          const question = getByField('code')({ data: questions, value: refQuestions[key].code });
          return result || (get(question, 'show') && !get(question, 'hidden'));
        };

        const resultShowGroup = reduce(
          Object.keys(refQuestions),
          (result, key) => reduceQuestionItem(result, key),
          false
        );
        if (show !== resultShowGroup) {
          dispatch({
            type: types.SET_SHOW_GROUP,
            id: groupId,
          });
        }
      };

      forEach(groups, groupsItem => getGroupItem(groupsItem));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleShowGroup: ${e.stack}`);
    }
  };

  /**
   * handle show Condition Of Questions
   */
  const handleShowQuestions = () => {
    try {
      dispatch({
        type: types.SET_SHOWVALUE_QUESTIONS,
        values,
        setFieldValue,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleShowQuestions: ${e.stack}`);
    }
  };

  /**
   * handle Code First Error Of Question
   */
  const handleCodeFirstError = ({ questions }) => {
    try {
      const codeFirstError = getCodeFirstError({ questions, errors });
      if (codeFirstError !== get(state, 'codeFirstError')) {
        dispatch({ type: types.SET_CODE_FIRST_ERROR, codeFirstError });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleCodeFirstError: ${e.stack}`);
    }
    return undefined;
  };

  /**
   * get answers of questions
   */
  const getAnswersQuestions = ({ parts, groups, questions }) => {
    try {
      // get question item
      const getQuestionItem = refQuestionsItem => {
        const question = getByField('code')({ data: questions, value: get(refQuestionsItem, 'code') });
        const code = get(question, 'code');
        const value = get(question, 'value');
        const path = get(question, 'path');
        return {
          code,
          value,
          path,
        };
      };

      // get group item
      const getGroupItem = refGroupItem => {
        const group = getByField('id')({ data: groups, value: get(refGroupItem, 'id') });
        const result = map(get(group, 'refQuestions'), refQuestionsItem => getQuestionItem(refQuestionsItem)).flat();
        return result;
      };

      // get part item
      const getPartItem = partItem => {
        const answers = map(get(partItem, 'refGroups'), refGroupItem => getGroupItem(refGroupItem)).flat();
        return {
          id: get(partItem, 'id'),
          answers,
        };
      };

      const partsResult = map(parts, partItem => getPartItem(partItem));
      const result = {};

      if (id) {
        result[id] = { parts: partsResult };
      } else {
        result.parts = partsResult;
      }

      return result;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getAnswersQuestions: ${e.stack}`);
    }
    return undefined;
  };

  // Callback from validate of GraduatedLoanBlock
  const eventEmitterLocal = val => {
    if (willValidate) setWillValidate(false);

    eventEmitter(val);
  };

  /**
   * add Additional Control for Detect Errores
   */
  const addAdditionalControlDetectErrores = () =>
    setTimeout(() => {
      setFieldValue();
    }, 100);

  /** *****
   *  useEffect
   ******* */
  useEffect(
    () => {
      // loop questions
      const questions = get(state, 'data.questions');
      const groups = get(state, 'data.groups');
      // const parts = get(state, 'data.parts');

      // handleShowQuestion
      // handleShowQuestion({ questions });

      handleShowQuestions();

      // handle Show Group
      handleShowGroup({ questions, groups });

      // handle code first error
      handleCodeFirstError({ questions });

      let isFormValid = isValid;

      if (id) {
        isFormValid = {};
        isFormValid[id] = isValid;
      }

      // Will validate GraduatedLoanBlock
      if (!willValidate) setWillValidate(true);

      // event emitter
      eventEmitter({
        isValid: isFormValid,
        nbErrors: size(errors),
        answers: getAnswersQuestions(get(state, 'data')),
      });

      // add additional control
      const timer = addAdditionalControlDetectErrores();
      return () => {
        clearTimeout(timer);
      };
    },
    //
    [state, errors]
  );

  useEffect(() => {
    // Update state only when necessary (find a better way of doing this)
    if (
      state?.data?.questions?.length !== initialState.data?.questions?.length ||
      some(
        get(state, 'data.groups', []),
        (g, idx) => get(g, 'refQuestions')?.length !== get(initialState, `data.groups[${idx}].refQuestions`)?.length
      )
    ) {
      dispatch({
        type: types.UPDATE,
        data: { ...state, data: initialState.data },
      });
    }
  }, [initialState]);

  useEffect(() => {
    if (willResetForm) {
      resetForm();

      // Because Formik resetForm does not update state
      dispatch({
        type: types.UPDATE,
        data: initialState,
      });

      // Because if we put a callback on onReset of Formik
      // Formik will call it at some point (when modifying initialState or initialValues I suppose)
      onResetForm({
        isValid,
        answers: getAnswersQuestions(get(initialState, 'data')),
      });
    }
  }, [willResetForm]);

  /** *****
   *  DOM
   ******* */

  /**
   * check exist error
   * @param {*} param0
   */
  const checkExistError = ({ code }) => indexOf(keys(errors), code) !== -1;

  /**
   * check show error
   * @param {*} param0
   */
  const checkShowError = ({ code }) => get(state, 'codeFirstError') === code;

  /**
   *  get question item
   * @param {*} param0
   */
  // eslint-disable-next-line react/prop-types
  const getQuestionItem = ({ refQuestionsItem }) => {
    try {
      // get question property
      const code = get(refQuestionsItem, 'code');
      const question = getByField('code')({ data: get(state, 'data.questions'), value: code });
      const selectionQuestion = get(question, 'selection');
      const digitsQuestion = get(question, 'digits');
      const webInputQuestion = get(question, 'webInput');
      const showQuestion = get(question, 'show');
      const hiddenQuestion = get(question, 'hidden');
      const typeQuestion = get(question, 'type');
      const validationQuestion = get(question, 'validation');
      const nameQuestion = get(question, 'name');
      const helpQuestion = get(question, 'help');
      const showHelpQuestion = get(question, 'showHelp');
      const showLabelQuestion = get(question, 'showLabel');
      const showError = checkShowError({ code });
      const existError = checkExistError({ code });
      const disabled = get(question, 'disabled');
      const param = get(question, 'param');
      const placeholder = get(question, 'placeholder');
      const componentCommon = get(question, 'componentCommon');

      // If necessary, add values to calculated field
      if (componentCommon && param?.operations) {
        param.operations = map(param.operations, operation => {
          if (operation?.constantCode) {
            return { ...operation, values: get(state, 'data.questions') };
          }

          return operation;
        });
      }

      // set value quesiton
      const setValueQuestion = v =>
        dispatch({
          type: types.SET_VALUE_QUESTION,
          code,
          value: getCorrectValue(v),
          setFieldValue,
        });
      // set show helper question
      const setShowHelperQuestion = () =>
        dispatch({
          type: types.SET_SHOWHELPER_QUESTION,
          code,
        });
      // call validate
      const callValidate = validate({
        code,
        type: typeQuestion,
        validation: validationQuestion,
      });
      // handle show error
      const handleShowError = code === get(state, 'codeFirstError') && (
        <ErrorCustom error={errors[get(state, 'codeFirstError')]} />
      );

      return (
        <Question
          key={code}
          show={showQuestion}
          hidden={hiddenQuestion}
          name={nameQuestion}
          help={helpQuestion}
          showHelp={showHelpQuestion}
          showLabel={showLabelQuestion}
          setShowHelperQuestion={setShowHelperQuestion}
        >
          <HandleFields
            blockId={id}
            code={code}
            selection={selectionQuestion}
            digits={digitsQuestion}
            thousandSeparator={i18n.thousandSeparator}
            decimalSeparator={i18n.decimalSeparator}
            type={typeQuestion}
            disabled={disabled}
            webInput={webInputQuestion}
            showError={showError}
            existError={existError}
            setValueQuestion={setValueQuestion}
            validate={callValidate}
            param={param}
            placeholder={placeholder}
            componentCommon={componentCommon}
          />
          {handleShowError}
        </Question>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getQuestionItem ${e.stack}`);
    }
    return <span />;
  };

  /**
   * get group item
   * @param {*} param0
   */
  // eslint-disable-next-line react/prop-types
  const getGroupItem = ({ refGroupsItem }) => {
    try {
      // get group property
      const groupId = get(refGroupsItem, 'id');
      const group = getByField('id')({ data: get(state, 'data.groups'), value: groupId });
      const titleGroup = getLabel({
        label: group?.title,
        strings,
        defaultString: '',
      });
      const tooltipGroup = get(group, 'tooltip');
      const showTooltipGroup = get(group, 'showTooltip');
      const descriptionGroup = get(group, 'description');
      const showGroup = get(group, 'show');
      // setShowTooltipGroup
      const setShowTooltipGroup = () => {
        dispatch({
          type: types.SET_SHOWTOOLTIP_GROUP,
          id: groupId,
        });
      };
      return (
        <Group
          key={groupId}
          id={groupId}
          title={titleGroup}
          tooltip={tooltipGroup}
          showTooltip={showTooltipGroup}
          description={descriptionGroup}
          show={showGroup}
          setShowTooltipGroup={setShowTooltipGroup}
        >
          {map(get(group, 'refQuestions'), refQuestionsItem => getQuestionItem({ refQuestionsItem }))}
        </Group>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getGroupItem ${e.stack}`);
    }
    return <span />;
  };

  /**
   * get part item
   * @param {*} param0
   */
  const getPartItem = ({ refPartsItem }) => {
    try {
      // get part property
      const partId = get(refPartsItem, 'id');
      const part = getByField('id')({ data: get(state, 'data.parts'), value: partId });
      const titlePart = getLabel({
        label: part?.title,
        strings,
        defaultString: '',
      });
      const showPart = get(part, 'show');
      const removePart = get(part, 'remove');
      const displayTitle = get(part, 'displayTitle', true);
      const helpQuestion = get(part, 'description');
      const showHelpQuestion = get(part, 'showHelp');
      // setShowPartie
      const setShowPartie = () =>
        dispatch({
          type: types.SET_SHOW_PART,
          id: partId,
        });
      // set show helper question
      const setShowHelperPart = () =>
        dispatch({
          type: types.SET_SHOWHELPER_PART,
          id: partId,
        });
      return (
        <Part
          key={partId}
          setShowPartie={setShowPartie}
          title={titlePart}
          show={showPart}
          remove={removePart}
          displayTitle={displayTitle}
          help={helpQuestion}
          showHelp={showHelpQuestion}
          setShowHelperPart={setShowHelperPart}
        >
          {map(get(part, 'refGroups'), refGroupsItem => getGroupItem({ refGroupsItem }))}
        </Part>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getPartItem ${e.stack}`);
    }
    return <span />;
  };
  const refParts = get(state, 'data.questionnaire.refParts', []);

  return (
    <div className={className}>
      <Form>{map(refParts, refPartsItem => getPartItem({ refPartsItem }))}</Form>
      {graduatedLoans && find(get(state, 'data.questions'), { code: 'loan_kind' })?.value === 'graduated' ? (
        <GraduatedLoanBlock
          graduatedLoans={graduatedLoans}
          loanId={id}
          strings={{}}
          eventEmitter={eventEmitterLocal}
          willResetForm={willResetForm}
          onResetForm={onResetForm}
          validateFormGraduatedLoans={validateFormGraduatedLoans}
          willValidate={willValidate}
          i18n={i18n}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

// todo optimize ...
QuestionnaireBlockCustom.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  initialState: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  graduatedLoans: PropTypes.array,
  formik: PropTypes.shape({
    values: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    errors: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    setFieldValue: PropTypes.func,
    isValid: PropTypes.bool,
    resetForm: PropTypes.func,
  }).isRequired,
  eventEmitter: PropTypes.func.isRequired,
  willResetForm: PropTypes.bool.isRequired,
  onResetForm: PropTypes.func.isRequired,
  className: PropTypes.string,
  validateFormGraduatedLoans: PropTypes.func,
  i18n: PropTypes.object,
  strings: PropTypes.object.isRequired,
};

QuestionnaireBlockCustom.defaultProps = {
  graduatedLoans: [],
  className: 'questionnaire-block-208650f4-00f8-4e7b-8825-dd064f4176a9',
  validateFormGraduatedLoans: undefined,
  i18n: {},
};

const QuestionnaireBlock = ({
  questionnaire,
  id,
  graduatedLoans,
  eventEmitter,
  willResetForm,
  onResetForm,
  className,
  validateFormGraduatedLoans,
  i18n,
  strings,
}) => {
  const initialState = initializeState({ questionnaire });

  return (
    <Formik
      initialValues={initializeQuestionsFormik({ questions: get(initialState, 'data.questions') })}
      // update the Form `initialValues` with new value
      enableReinitialize
    >
      {({ setFieldValue, values, errors, isValid, resetForm }) => (
        <QuestionnaireBlockCustom
          initialState={initialState}
          id={id}
          graduatedLoans={graduatedLoans}
          validateFormGraduatedLoans={validateFormGraduatedLoans}
          eventEmitter={eventEmitter}
          formik={{ errors, setFieldValue, values, isValid, resetForm }}
          willResetForm={willResetForm}
          onResetForm={onResetForm}
          className={className}
          i18n={i18n}
          strings={strings}
        />
      )}
    </Formik>
  );
};

QuestionnaireBlock.propTypes = {
  questionnaire: questionnaireShape.isRequired,
  id: PropTypes.string,
  graduatedLoans: PropTypes.array,
  eventEmitter: PropTypes.func.isRequired,
  willResetForm: PropTypes.bool,
  onResetForm: PropTypes.func,
  className: PropTypes.string,
  validateFormGraduatedLoans: PropTypes.func,
  i18n: PropTypes.object,
  strings: PropTypes.object.isRequired,
};

QuestionnaireBlock.defaultProps = {
  id: null,
  graduatedLoans: [],
  willResetForm: false,
  onResetForm: () => {},
  className: undefined,
  validateFormGraduatedLoans: undefined,
  i18n: {},
};

export default QuestionnaireBlock;
